import { toast as sonnerToast } from 'sonner';
import type { ApiError } from '../api/errors';

/** `ErrorResponse.details` da validação é `{campo: motivo}`. */
const fieldDetails = (details: unknown): string | undefined => {
  if (details === null || typeof details !== 'object') return undefined;
  const parts = Object.entries(details as Record<string, unknown>)
    .filter(([, v]) => typeof v === 'string')
    .map(([field, why]) => `${field}: ${String(why)}`);
  return parts.length > 0 ? parts.join(' · ') : undefined;
};

/**
 * Thin façade over sonner's `toast` API.
 *
 * The goal is to keep feature code on a single import (`@/lib/toast`) so
 * we can later swap the underlying library, change the call signature,
 * or add cross-cutting behavior (telemetry, dedup) without rewriting
 * every callsite.
 *
 * Helpers normalize the most common pattern in this codebase — a short
 * title plus an optional secondary description — and add `fromApiError`,
 * which folds an `ApiError` into the visual `(message, code)` pair the
 * UX expects. The raw sonner namespace is intentionally not re-exported
 * verbatim: callers that need advanced features (custom JSX, action
 * buttons) can import from `sonner` directly until we have a real need
 * to wrap them.
 */
export const toast = {
  success: (msg: string, description?: string): string | number =>
    sonnerToast.success(msg, description ? { description } : undefined),
  error: (msg: string, description?: string): string | number =>
    sonnerToast.error(msg, description ? { description } : undefined),
  warning: (msg: string, description?: string): string | number =>
    sonnerToast.warning(msg, description ? { description } : undefined),
  info: (msg: string, description?: string): string | number =>
    sonnerToast.info(msg, description ? { description } : undefined),
  /**
   * Render an error toast from a structured `ApiError`.
   *
   * Uses `error.message` as the title (already the localized string
   * coming from the backend's `ErrorResponse`) and the stable
   * `error.code` (e.g. `CANCEL_WINDOW_EXPIRED`) as the description so
   * it remains visible without flooding the title line. Falls back to
   * `fallbackMsg` when the error payload doesn't carry a message.
   *
   * Em `VALIDATION_ERROR` o título é o genérico "Verifique os campos
   * destacados." — sem dizer QUAL campo, ele manda o operador procurar um
   * destaque que a maioria das telas não renderiza. Aí a descrição vira o
   * `details` (`{campo: motivo}`), que é o motivo real que o backend mandou.
   */
  fromApiError: (error: ApiError, fallbackMsg = 'Algo deu errado'): string | number =>
    sonnerToast.error(error.message ?? fallbackMsg, {
      description:
        (error.code === 'VALIDATION_ERROR' ? fieldDetails(error.details) : undefined) ??
        error.code ??
        undefined,
    }),
  /** Re-export of `sonner.toast.promise` for parity with the sonner API. */
  promise: sonnerToast.promise.bind(sonnerToast),
};
